/**
 * Чистая логика экрана «Воронка»: состояние экрана → запрос списка CRM, итоги этапов → полоса,
 * выбор воронки по умолчанию, подписи. Ни сети, ни React — проверяется отдельно
 * (__tests__/pipelineQuery.test.ts).
 */
import type { CardStage } from '@/api/crmCard';
import type { DealRowData, PipelineItem, StageTotal } from '@/api/pipeline';
import { formatMoney } from './dealCard';

export const PAGE_SIZE = 30;
/** Закрытые этапы («Успех», «Отказ») — только последние 30 дней, как на доске CRM. */
export const CLOSED_WITHIN_DAYS = 30;

export interface PipelineView {
  pipelineId: string;
  /** Мои сделки (и ничьи — их можно взять) или все сделки воронки (только руководителю/администратору). */
  mine: boolean;
  /** Мой глобальный идентификатор; без него «мои» отбираются старым способом owner=me (без ничьих). */
  meId: string | null;
  missed: boolean;
  overdue: boolean;
  stageId: string | null;
}

/**
 * Состояние экрана → параметры GET /api/crm/deals (GiperCRM domain/dealListQuery.parseDealListQuery):
 *   pipelineId, stageIds, ownerIds=<я>,none («мои и ничьи»), missedIncoming=1, overdueTask=1,
 *   closedWithinDays, sort=updatedAt&dir=desc, page, limit.
 */
export function dealListParams(v: PipelineView, page = 1, limit = PAGE_SIZE): URLSearchParams {
  const p = new URLSearchParams();
  p.set('pipelineId', v.pipelineId);
  if (v.stageId) p.set('stageIds', v.stageId);
  if (v.mine) {
    if (v.meId) p.set('ownerIds', `${v.meId},none`);
    else p.set('owner', 'me');
  }
  if (v.missed) p.set('missedIncoming', '1');
  if (v.overdue) p.set('overdueTask', '1');
  p.set('closedWithinDays', String(CLOSED_WITHIN_DAYS));
  p.set('sort', 'updatedAt');
  p.set('dir', 'desc');
  p.set('page', String(page));
  p.set('limit', String(limit));
  return p;
}

/** Только счётчик для значка («сколько с пропущенными»): тот же отбор, одна строка. */
export function countParams(v: PipelineView, flag: 'missed' | 'overdue'): URLSearchParams {
  return dealListParams({ ...v, stageId: null, missed: flag === 'missed', overdue: flag === 'overdue' }, 1, 1);
}

/** Есть ли следующая страница. */
export function nextPageOf(last: { page: number; limit: number; total: number }): number | undefined {
  return last.page * last.limit < last.total ? last.page + 1 : undefined;
}

// ── Полоса воронки ──

export interface SpectrumSegment { id: string; name: string; color: string; count: number }

export interface SpectrumModel {
  segments: SpectrumSegment[];
  /** Сделок в выборке (все этапы или выбранный). */
  count: number;
  /** Сумма словами по валютам: «12 500 $ · 3 000 000 сум»; пусто — «». */
  sumText: string;
  selected: SpectrumSegment | null;
}

/**
 * Итоги этапов CRM (по валютам) → отрезки одинаковой ширины по каждому этапу воронки, число в отрезке,
 * подпись «N сделок, сумма». Этап без сделок — 0 (а не пропавший отрезок).
 */
export function spectrumModel(stages: CardStage[], totals: StageTotal[], selectedId: string | null): SpectrumModel {
  const ordered = [...stages].sort((a, b) => a.position - b.position);
  const countOf = new Map<string, number>();
  for (const t of totals) countOf.set(t.stageId, (countOf.get(t.stageId) ?? 0) + t.count);
  const segments = ordered.map((s) => ({ id: s.id, name: s.name, color: s.color || '#0abab5', count: countOf.get(s.id) ?? 0 }));
  const selected = segments.find((s) => s.id === selectedId) ?? null;
  const inScope = totals.filter((t) => !selected || t.stageId === selected.id);
  const byCur = new Map<string, number>();
  for (const t of inScope) byCur.set(t.currency, (byCur.get(t.currency) ?? 0) + t.amount);
  const sumText = [...byCur.entries()]
    .filter(([, a]) => a > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([cur, a]) => formatMoney(a, cur))
    .join(' · ');
  const count = selected ? selected.count : segments.reduce((a, s) => a + s.count, 0);
  return { segments, count, sumText, selected };
}

// ── Выбор воронки ──

/**
 * Какую воронку показать: сохранённую (если она ещё доступна), иначе основную (home-pipeline), иначе
 * воронку «по умолчанию» организации, иначе первую. Сохранённой, которой нет в списке, не доверяем.
 */
export function pickPipeline(list: PipelineItem[], saved: string | null, home: string | null): PipelineItem | undefined {
  return list.find((p) => p.id === saved)
    ?? list.find((p) => p.id === home)
    ?? list.find((p) => p.isDefault)
    ?? list[0];
}

/** Подпись воронки в окне выбора: «Отдел продаж, 12 сделок» / «общая, 3 сделки». */
export function pipelineNote(p: PipelineItem): string {
  const n = p.visibleDeals ?? p._count?.deals;
  const deps = (p.departments ?? []).map((d) => d.name).filter(Boolean) as string[];
  const who = deps.length
    ? deps.join(', ')
    : p.departments && !(p.memberCount ?? 0)
      ? 'общая'
      : p.memberCount
        ? 'по приглашению'
        : '';
  const cnt = n === undefined ? '' : `${n} ${plural(n, 'сделка', 'сделки', 'сделок')}`;
  return [who, cnt].filter(Boolean).join(', ');
}

export function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Руководитель или администратор: может смотреть «Все сделки воронки» и видит все воронки ветки. */
export function isLead(scopeKind: string | undefined): boolean {
  return scopeKind === 'all' || scopeKind === 'branch';
}

// ── Строка сделки ──

export interface DealRowModel {
  title: string;
  subtitle: string;
  amountText: string;
  stageColor: string;
  /** Пропущенные входящие — клиент звонил, перезвоните (красным). */
  missed: number;
  /** Клиент написал и ждёт ответа. */
  awaiting: boolean;
  task: { text: string; late: boolean } | null;
  /** Лид в «Новых»: можно принять или отсеять. */
  isLead: boolean;
  /** Есть привязанная переписка — открываем её, иначе отдельный экран сделки. */
  conversation: { accountId: number; displayId: number } | null;
}

const WEEKDAY = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];

/** «сегодня в 15:00» / «вчера» / «пн, 14 окт» — срок ближайшей задачи. */
export function taskWhen(at: string, allDay: boolean, now: Date = new Date()): string {
  const d = allDay ? new Date(`${at.slice(0, 10)}T00:00:00`) : new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  const day0 = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day0(d) - day0(now)) / 86_400_000);
  const dayText = diff === 0 ? 'сегодня' : diff === 1 ? 'завтра' : diff === -1 ? 'вчера'
    : `${WEEKDAY[d.getDay()]}, ${d.getDate()} ${d.toLocaleDateString('ru-RU', { month: 'short' }).replace('.', '')}`;
  if (allDay) return dayText;
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  return `${dayText} в ${time}`;
}

export function dealRowModel(d: DealRowData, stages: CardStage[], ownerName: (id: string) => string | null, now: Date = new Date()): DealRowModel {
  const stage = d.stage ?? stages.find((s) => s.id === d.stageId) ?? null;
  const person = d.person ? [d.person.firstName, d.person.lastName].filter(Boolean).join(' ').trim() : '';
  const title = d.company?.name || person || d.title;
  // Подпись: название сделки (если заголовок — компания/человек) и ответственный, если не я.
  const parts: string[] = [];
  if (d.title && d.title !== title) parts.push(d.title);
  else if (person && person !== title) parts.push(person);
  if (!d.ownerId) parts.push('без ответственного');
  else {
    const who = ownerName(d.ownerId);
    if (who) parts.push(who);
  }
  const t = d.nextTask ?? null;
  return {
    title,
    subtitle: parts.join(', '),
    amountText: formatMoney(d.amount, d.currency),
    stageColor: stage?.color || '#0abab5',
    missed: d.callStats?.missedIncoming ?? 0,
    awaiting: !!d.awaitingReplySince && !d.closedAt,
    task: t ? { text: `${t.title.split(':')[0]}, ${taskWhen(t.at, t.allDay, now)}`, late: t.overdue } : null,
    isLead: stage?.stageType === 'lead' && !d.closedAt,
    conversation: d.chatwootAccountId && d.chatwootDisplayId
      ? { accountId: d.chatwootAccountId, displayId: d.chatwootDisplayId }
      : null,
  };
}
